import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import rotanaService from '../services/rotanaService';
import { useAuth } from '../contexts/AuthContext';
import ConfirmModal from './shared/ConfirmModal';
import CopyButton from './shared/CopyButton';
import DiffModal from './shared/DiffModal';
import { formatMessage } from './shared/messageLogUtils';
import './RotanaPage.css';
import './PaymentPorterPage.css';

const isConnectorActive = (status) => (status || '').toLowerCase() === 'active';

// "when did this pipe last run" in the fewest characters that still answer it. A date on its own is
// what the row needs — the exact timestamp is one click away in the log itself.
const formatSeen = (iso) => {
    if (!iso) return 'never';
    const then = new Date(iso);
    if (Number.isNaN(then.getTime())) return String(iso).slice(0, 10);
    const mins = Math.floor((Date.now() - then.getTime()) / 60000);
    if (mins < 1)    return 'just now';
    if (mins < 60)   return `${mins}m ago`;
    if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
    return then.toISOString().slice(0, 10);   // yyyy-mm-dd, per the house date format
};

// Name / #id / status block — shared by source, target and unrouted rows.
//
// connectorType IS NOT SHOWN. Every connector in this account is stored as 'INBOUND', including both
// halves of a route, so the badge said the same thing everywhere and said it misleadingly: 2865 and 2763
// are each INBOUND, which reads as "neither of these sends anything" when in fact each one receives from
// its own external system and forwards to the other. It is a legacy column. Direction that actually means
// something is already conveyed by the route grouping and the → marker on the target row.
const ConnectorMeta = ({ c }) => (
    <div className="rotana-connector-meta">
        <span className="rotana-connector-name">
            {c.name || `Connector ${c.id}`}
            <span className="rotana-connector-id"> #{c.id}</span>
        </span>
        {c.marketplace && <span className="rotana-connector-marketplace">{c.marketplace}</span>}
        {c.lambda && (
            <span className="rotana-connector-detail" title={c.lambda}>
                <span className="rotana-detail-label">λ</span> {c.lambda}
            </span>
        )}
        {c.sqs_queue && (
            <span className="rotana-connector-detail" title={c.sqs_queue}>
                <span className="rotana-detail-label">Q</span> {c.sqs_queue}
            </span>
        )}
        <span className={`rotana-connector-status ${isConnectorActive(c.status) ? 'active' : 'inactive'}`}>
            {c.status || 'Unknown'}
        </span>
    </div>
);

// Build the source -> target tree from the flat connectors list + routes.
// A route row is (action, target); group by source, then by action.
const buildConnectorTree = (connectors, routes) => {
    const byId = new Map(connectors.map(c => [c.id, c]));
    const resolve = (id, name, type, status, marketplace, sqsQueue, lambda) =>
        byId.get(id) || { id, name, type, status, marketplace, sqs_queue: sqsQueue, lambda };

    const sources = new Map();      // source_id -> { connector, actions: Map }
    const routedIds = new Set();

    for (const r of routes) {
        routedIds.add(r.source_id);
        routedIds.add(r.target_id);

        if (!sources.has(r.source_id)) {
            sources.set(r.source_id, {
                connector: resolve(r.source_id, r.source_name, r.source_type, r.source_status, r.source_marketplace, r.source_sqs_queue, r.source_lambda),
                actions: new Map(),
            });
        }
        const grp = sources.get(r.source_id);
        if (!grp.actions.has(r.action_id)) {
            grp.actions.set(r.action_id, {
                actionId: r.action_id,
                description: r.action_description,
                status: r.action_status,
                targets: [],
            });
        }
        grp.actions.get(r.action_id).targets.push(
            resolve(r.target_id, r.target_name, r.target_type, r.target_status, r.target_marketplace, r.target_sqs_queue, r.target_lambda)
        );
    }

    const byName = (a, b) => (a.connector?.name || a.name || '').localeCompare(b.connector?.name || b.name || '');

    const sourceGroups = [...sources.values()]
        .map(g => ({ connector: g.connector, actions: [...g.actions.values()] }))
        .sort(byName);

    const unrouted = connectors.filter(c => !routedIds.has(c.id)).sort(byName);

    return { sourceGroups, unrouted };
};

const directionClass = (d) => {
    const v = (d || '').toUpperCase();
    if (v === 'ENTRY') return 'rotana-direction-entry';
    if (v === 'EXIT')  return 'rotana-direction-exit';
    return 'rotana-direction-other';
};

const tryParseJsonStrings = (val, depth = 0) => {
    if (depth > 5) return val;
    if (typeof val === 'string') {
        const trimmed = val.trim();
        if ((trimmed.startsWith('{') || trimmed.startsWith('[')) && trimmed.length > 2) {
            try { return tryParseJsonStrings(JSON.parse(trimmed), depth + 1); } catch { /* not JSON */ }
        }
        return val;
    }
    if (Array.isArray(val)) return val.map(v => tryParseJsonStrings(v, depth + 1));
    if (val && typeof val === 'object') {
        const out = {};
        for (const [k, v] of Object.entries(val)) out[k] = tryParseJsonStrings(v, depth + 1);
        return out;
    }
    return val;
};

const decodePayload = (b64) => {
    if (!b64) return null;
    try {
        // UTF-8, not atob's Latin-1: "—" in a logged error came out as "â€" (2026-09-30).
        const str = new TextDecoder('utf-8').decode(Uint8Array.from(atob(b64), c => c.charCodeAt(0)));
        const trimmed = str.trimStart();
        if (trimmed.startsWith('<')) return str;
        return tryParseJsonStrings(JSON.parse(str));
    } catch {
        return b64;
    }
};

// XML arrives as one unbroken line and JSON as an object — both go through the shared formatter so the
// pane, the copy button and the diff all show the SAME indented text. They must agree: a diff computed on
// unformatted XML is a single +/- pair covering the whole document, which tells you nothing.
const formatPayload = (decoded) => {
    if (!decoded) return 'No payload';
    if (typeof decoded === 'string') return formatMessage(decoded);
    return JSON.stringify(decoded, null, 2);
};

// Pair entry/exit records by lambda within a transaction group, ORDERED ALONG THE PIPELINE.
//
// The rows used to come out in Map insertion order, which inherits the Athena `ORDER BY timestamp DESC`
// used to list newest transactions first. Within one transaction that is backwards: the receiving lambda
// appeared BELOW the one it fed, so a Pegasus → SynXis message read bottom-to-top and the hop order had to
// be inferred from the lambda names.
//
// Sorted by each leg's own earliest record instead, ascending, so a message reads in the order it actually
// travelled. ISO-8601 timestamps compare correctly as strings, and they carry milliseconds even though the
// row only renders to the second — which is what separates two legs that both land inside the same second,
// as an inbound hand-off to its consumer usually does.
const legStart = (pair) => {
    const stamps = [pair.entry, pair.exit, pair.error]
        .filter(Boolean)
        .map(l => l.timestamp)
        .filter(Boolean);
    return stamps.length ? stamps.sort()[0] : '';
};

const pairLogs = (logs) => {
    const byLambda = new Map();
    for (const log of logs) {
        if (!byLambda.has(log.lambda)) byLambda.set(log.lambda, {});
        const dir = (log.direction || '').toLowerCase();
        byLambda.get(log.lambda)[dir] = log;
    }
    return [...byLambda.entries()]
        .map(([lambda, pair]) => ({ lambda, ...pair }))
        .sort((a, b) => legStart(a).localeCompare(legStart(b)));
};

// The formatted text is computed ONCE and used for both the pane and the copy button, so what lands on the
// clipboard is exactly what is on screen — copying the raw single-line original would be a surprise.
const PayloadSection = ({ label, labelClass, decoded }) => {
    const text = formatPayload(decoded);
    return (
        <div className="rotana-payload-col">
            <div className="rotana-payload-head">
                <div className={`rotana-payload-label ${labelClass}`}>{label}</div>
                {decoded && (
                    <CopyButton value={text} label="Copy" className="rotana-payload-copy" title={`Copy ${label} payload`} />
                )}
            </div>
            <pre className="rotana-log-payload-pre">{text}</pre>
        </div>
    );
};

// Split one transaction's legs into a block per connector.
//
// A transaction that crossed the channel is a conversation between two or three connectors, and the API
// returns their records interleaved by time. Rendered flat that reads as one stream from one place —
// which is how a message that arrived somewhere and was never forwarded stays invisible. An indent alone
// was not enough either: it says "this row is different" without saying WHICH connector it belongs to,
// so the reader still has to decode a bare #id at the end of the line.
//
// The block is labelled with the connector's name, and the one you selected leads. Array sort is stable,
// so within a block the records stay in the order they happened.
// THE CONNECTOR PICKER.
//
// This replaces a permanently-open tree down the left of the screen. That tree listed every source, every
// route beneath it and every target beneath that — roughly two hundred rows for this chain — and it was
// on screen the whole time whether or not you were choosing anything. The thing being chosen is chosen
// once and then not touched again for the rest of the session, so it does not deserve a third of the
// width.
//
// A PARENT IS THE UNIT OF CHOICE. Picking a source selects it AND everything it routes to, because a
// channel's records are filed under whichever connector received them: what OHIP sent sits under OHIP,
// what Pegasus received sits under Pegasus. Choosing one half and seeing one half is what made a
// message that arrived and was never forwarded impossible to spot.
//
// UNROUTED CONNECTORS AND PIPES ARE STILL IN THE LIST. They were in the old tree and nothing else on the
// site reaches them, so dropping them here would strand them. They are grouped under their own headings
// rather than mixed in, because they answer a different question from "show me this channel".
const ConnectorPicker = ({
    groups, unrouted, gateways, query, onQuery, showInactive, onShowInactive,
    label, loading, error, onPickGroup, onPickConnector, onPickGateway,
}) => {
    const [open, setOpen] = useState(false);
    const box = useRef(null);
    const field = useRef(null);

    // Click-away and Escape both close it. A dropdown that can only be dismissed by choosing something
    // forces a selection on someone who opened it to look.
    useEffect(() => {
        if (!open) return;
        const onDown = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
        const onKey  = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
    }, [open]);

    useEffect(() => { if (open && field.current) field.current.focus(); }, [open]);

    const empty = !loading && !error && groups.length === 0 && unrouted.length === 0 && gateways.length === 0;

    return (
        <div className="rotana-picker" ref={box}>
            <button
                type="button"
                className={`rotana-picker-trigger${open ? ' rotana-picker-open' : ''}`}
                onClick={() => setOpen(o => !o)}
                title="Choose a connector to search"
            >
                <span className={`rotana-picker-value${label ? '' : ' rotana-picker-placeholder'}`}>
                    {label || 'Choose a connector…'}
                </span>
                <span className="rotana-picker-caret" aria-hidden="true">▾</span>
            </button>

            {open && (
                <div className="rotana-picker-panel">
                    <div className="rotana-picker-search">
                        <input
                            ref={field}
                            className="rotana-connector-search"
                            placeholder="Search by name, id, property, marketplace or lambda…"
                            value={query}
                            onChange={e => onQuery(e.target.value)}
                        />
                        <label className="rotana-inactive-toggle">
                            <input type="checkbox" checked={showInactive} onChange={e => onShowInactive(e.target.checked)} />
                            Show inactive
                        </label>
                    </div>

                    <div className="rotana-picker-list">
                        {loading && <div className="rotana-loading">Loading…</div>}
                        {error   && <div className="rotana-error">{error}</div>}
                        {empty   && <div className="rotana-empty">Nothing matches that.</div>}

                        {groups.length > 0 && <div className="rotana-group-divider">Connectors</div>}
                        {groups.map(g => {
                            const src = g.connector;
                            const targets = g.actions.flatMap(a => a.targets);
                            return (
                                <div
                                    key={`g-${src.id}`}
                                    className={`rotana-connector-item${isConnectorActive(src.status) ? '' : ' rotana-dim'}`}
                                    onClick={() => { setOpen(false); onPickGroup(g); }}
                                >
                                    <ConnectorMeta c={src} />
                                    {/* WHAT COMES WITH IT. Selecting a parent widens the search to its
                                        targets, and that is not guessable from the parent's name — the
                                        row has to say so before the click, not after. */}
                                    {targets.length > 0 && (
                                        <span className="rotana-picker-kids" title={targets.map(t => `${t.name} #${t.id}`).join('\n')}>
                                            +{targets.length}
                                        </span>
                                    )}
                                </div>
                            );
                        })}

                        {unrouted.length > 0 && <div className="rotana-group-divider">Unrouted</div>}
                        {unrouted.map(c => (
                            <div
                                key={`u-${c.id}`}
                                className={`rotana-connector-item${isConnectorActive(c.status) ? '' : ' rotana-dim'}`}
                                onClick={() => { setOpen(false); onPickConnector(c.id); }}
                            >
                                <ConnectorMeta c={c} />
                            </div>
                        ))}

                        {gateways.length > 0 && <div className="rotana-group-divider">Message pipes</div>}
                        {gateways.map(g => (
                            <div
                                key={`p-${g.lambda}`}
                                className="rotana-connector-item rotana-gateway-item"
                                onClick={() => { setOpen(false); onPickGateway(g); }}
                                title={`${g.messages} record(s) across ${g.connectors} connector(s)`}
                            >
                                <div className="rotana-gateway-meta">
                                    <div className="rotana-gateway-name">λ {g.lambda}</div>
                                    <div className="rotana-gateway-sub">
                                        {g.messages} msg{g.messages === 1 ? '' : 's'}
                                        {g.errors > 0 && <span className="rotana-gateway-errors"> · {g.errors} error{g.errors === 1 ? '' : 's'}</span>}
                                        {g.lastSeen && <span className="rotana-gateway-seen"> · {formatSeen(g.lastSeen)}</span>}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const partitionByConnector = (pairs, selectedId) => {
    const order = [];
    const byConnector = new Map();
    for (const pair of pairs) {
        const ref = pair.exit || pair.error || pair.entry;
        const id = String(ref?.connectorId ?? '');
        if (!byConnector.has(id)) { byConnector.set(id, []); order.push(id); }
        byConnector.get(id).push(pair);
    }
    const primary = String(selectedId ?? '');
    order.sort((a, b) => (a === primary ? 0 : 1) - (b === primary ? 0 : 1));
    return order.map(id => ({ connectorId: id, pairs: byConnector.get(id) }));
};

const LogPairRow = ({ pair, connectorId, connectorIds, onFollow }) => {
    const [open, setOpen] = useState(false);
    const [payloads, setPayloads] = useState(null);   // { '<lambda>|<direction>': b64 }
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState(null);
    const [diffOpen, setDiffOpen] = useState(false);
    const ref      = pair.exit || pair.error || pair.entry;
    const hasError = !!pair.error;
    // `connectorId` is the SELECTED connector — the parent when a source group is selected. A record
    // filed under any other connector in the group is a child of it.

    // Payloads are fetched on FIRST expand, not shipped with the list — see rotanaService.getLogPayloads.
    // Cached per transaction, so collapsing and re-opening costs nothing.
    //
    // NEITHER `loading` NOR `payloads` MAY BE A DEPENDENCY, and the cleanup must not cancel the request.
    // With `loading` in the deps this effect cancelled its own fetch: it calls setLoading(true), that state
    // change re-runs the effect, the cleanup fires and sets `cancelled = true` on the closure belonging to
    // the invocation that owns the in-flight request. The response then arrived — visibly, in the network
    // tab — and every setter was skipped, leaving the row on "Loading payload…" permanently.
    //
    // A ref, not state, guards the one-fetch-per-transaction rule: it updates synchronously and does not
    // re-render, so it cannot feed back into the effect the way the state flags did. Collapsing a row
    // mid-flight must not abandon `loading` at true either, so the only thing that suppresses a late
    // setState is unmount.
    const fetchedTxn = useRef(null);
    const mounted = useRef(true);
    useEffect(() => () => { mounted.current = false; }, []);

    useEffect(() => {
        const txn = ref?.transactionId;
        if (!open || !txn || fetchedTxn.current === txn) return;
        fetchedTxn.current = txn;
        setLoading(true);
        setError(null);
        rotanaService.getLogPayloads(connectorId, txn, connectorIds)
            .then(data => {
                if (!mounted.current) return;
                const map = {};
                for (const p of data.payloads || []) {
                    map[`${p.lambda}|${(p.direction || '').toLowerCase()}`] = p.payload;
                }
                setPayloads(map);
            })
            .catch(() => {
                if (!mounted.current) return;
                fetchedTxn.current = null;   // let a re-expand retry a failed fetch
                setError('Failed to load payload');
            })
            .finally(() => { if (mounted.current) setLoading(false); });
    }, [open, connectorId, connectorIds, ref?.transactionId]);

    const payloadFor = (log) => (log && payloads) ? payloads[`${log.lambda}|${(log.direction || '').toLowerCase()}`] : null;
    // THE ERROR WINS. A leg can carry both — a lambda that logged the refusal and then, on its way out,
    // stamped an exit with no payload — and showing the exit under the "Error" label read as "No payload"
    // while the record holding OHIP's answer sat unseen beside it (connector 3316, 2026-09-30).
    const exitLog = pair.error || pair.exit;

    const entryDecoded = open && payloadFor(pair.entry) ? decodePayload(payloadFor(pair.entry)) : null;
    const exitDecoded  = open && payloadFor(exitLog)    ? decodePayload(payloadFor(exitLog))    : null;

    return (
        // NO INDENT AND NO ELBOW HERE ANY MORE. Both were standing in for a connector name the row did
        // not have; the block this row now sits in carries that name in words, so repeating it as
        // decoration on every line only competes with it.
        <div className="rotana-log-entry">
            <div className="rotana-log-entry-header" onClick={() => setOpen(o => !o)}>
                <span className={`rotana-direction-badge ${pair.entry ? 'rotana-direction-entry' : 'rotana-direction-other'}`}>IN</span>
                <span className={`rotana-direction-badge ${hasError ? 'rotana-direction-error' : pair.exit ? 'rotana-direction-exit' : 'rotana-direction-other'}`}>
                    {hasError ? 'ERR' : pair.exit ? 'OUT' : '…'}
                </span>
                <span className="rotana-log-msgtype">{ref?.messageType || '—'}</span>
                {/* WHICH LEG THIS IS. A transaction that crossed the channel has one row per lambda —
                    the inbound receiver and the onward sender. Seeing only one row for a transaction is
                    how you spot a message that arrived and was never forwarded, which is invisible if the
                    lambda isn't named. */}
                <span className="rotana-log-lambda" title={`${pair.lambda || 'unknown lambda'}${ref?.connectorId ? ` · connector ${ref.connectorId}` : ''}`}>
                    {pair.lambda || '—'}
                    {ref?.connectorId && <span className="rotana-log-lambda-conn"> #{ref.connectorId}</span>}
                </span>
                <span className="rotana-log-connector" title="Source → Target">
                    {ref?.sourceConnectorId || '—'} → {ref?.targetConnectorId || '—'}
                </span>
                <span className="rotana-log-txn" title={ref?.transactionId}>{ref?.transactionId}</span>
                {/* FOLLOW THIS MESSAGE THROUGH THE PIPELINE.
                    Offered only when the record carries the hop-to-hop id — records written before the
                    connectors began setting it, and traffic a connector originated itself, have nothing
                    to follow, and a button that returns one row would read as "it went nowhere" rather
                    than "we cannot tell".
                    stopPropagation because the row header is itself the expand/collapse control. */}
                {ref?.hcTransactionId && onFollow && (
                    <button
                        type="button"
                        className="rotana-follow-btn"
                        title={`Follow this message across every connector · ${ref.hcTransactionId}`}
                        onClick={(e) => { e.stopPropagation(); onFollow(ref.hcTransactionId); }}
                    >
                        Follow →
                    </button>
                )}
                <span className="rotana-log-time">{ref?.timestamp ? new Date(ref.timestamp).toLocaleString() : '—'}</span>
                <span className="rotana-log-expand">{open ? '▲' : '▼'}</span>
            </div>
            {open && (
                <>
                    {!loading && !error && entryDecoded && exitDecoded && (
                        <div className="rotana-log-payload-actions">
                            <button
                                type="button"
                                className="rotana-diff-btn"
                                onClick={() => setDiffOpen(true)}
                                title="Compare what arrived with what was sent on"
                            >
                                Diff entry → {hasError ? 'error' : 'exit'}
                            </button>
                        </div>
                    )}
                    <div className="rotana-log-payload rotana-log-payload-pair">
                        {loading && <div className="rotana-payload-col">Loading payload…</div>}
                        {error   && <div className="rotana-payload-col rotana-direction-error">{error}</div>}
                        {!loading && !error && pair.entry && (
                            <PayloadSection label="Entry" labelClass="rotana-direction-entry" decoded={entryDecoded} />
                        )}
                        {!loading && !error && exitLog && (
                            <PayloadSection
                                label={hasError ? 'Error' : 'Exit'}
                                labelClass={hasError ? 'rotana-direction-error' : 'rotana-direction-exit'}
                                decoded={exitDecoded}
                            />
                        )}
                    </div>
                </>
            )}
            {diffOpen && (
                <DiffModal
                    title="Entry → Exit"
                    subtitle={ref?.transactionId}
                    leftLabel="Entry"
                    rightLabel={hasError ? 'Error' : 'Exit'}
                    left={formatPayload(entryDecoded)}
                    right={formatPayload(exitDecoded)}
                    onClose={() => setDiffOpen(false)}
                />
            )}
        </div>
    );
};

const groupByTransaction = (logs) => {
    const groups = new Map();
    for (const log of logs) {
        if (!groups.has(log.transactionId)) groups.set(log.transactionId, []);
        groups.get(log.transactionId).push(log);
    }
    // preserve Athena DESC order — first entry in group determines group position
    return [...groups.entries()];
};

const RotanaPage = () => {
    const { isSuperAdmin } = useAuth();
    const [connectors, setConnectors]       = useState([]);
    const [routes, setRoutes]               = useState([]);
    // The message pipes: lambdas that have written to this chain's log. Derived, not configured — see
    // the gateway query in the rotana lambda for why.
    const [gateways, setGateways]           = useState([]);
    const [selectedLambda, setSelectedLambda] = useState(null);
    const [dbUser, setDbUser]               = useState(null);
    const [connectorsLoading, setConnectorsLoading] = useState(true);
    const [connectorsError, setConnectorsError]     = useState(null);

    const [connectorSearch, setConnectorSearch] = useState('');
    const [showInactive, setShowInactive] = useState(false);

    // selectedId is the connector the log route is addressed to; selectedIds is everything the query
    // covers. They differ only when a ROUTE is selected, where the list spans both of its connectors
    // because each half files the direction it received. selectedLabel names the route in the header.
    const [selectedId, setSelectedId]   = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [selectedLabel, setSelectedLabel] = useState(null);
    const [logs, setLogs]               = useState([]);
    // The message being followed across connectors, or null when browsing one connector's own log.
    const [trail, setTrail]             = useState(null);
    const [logsLoading, setLogsLoading]   = useState(false);
    const [logsError, setLogsError]       = useState(null);
    const [purging, setPurging]           = useState(false);
    const [confirmModal, setConfirmModal] = useState(null);

    // Jira (superadmin). Tickets created this session are kept per transaction so the button becomes a
    // link to what it just made — raising a second ticket for the same transaction by mistake is the
    // easy error here, and a button that still says "Jira" invites it.
    const [jiraTickets, setJiraTickets]   = useState({});   // transactionId -> { key, url }
    const [jiraTxn, setJiraTxn]           = useState(null); // transaction the modal is open for
    const [jiraNote, setJiraNote]         = useState('');
    const [jiraCreating, setJiraCreating] = useState(null); // transactionId currently being raised
    // { txnId, message } — scoped to the group it belongs to. A bare string rendered inside the map
    // would repeat the same failure under every transaction on the page.
    const [jiraError, setJiraError]       = useState(null);

    const [filterMsgType,  setFilterMsgType]  = useState('');
    const [filterPayload,  setFilterPayload]  = useState('');
    // Transaction id search. The lambda has always supported ?transactionId= (a LIKE match, so a partial
    // id works); nothing was ever wired to it. Pasting an id straight off a row — or out of the
    // [logConnectorRecord] line in CloudWatch, where it is embedded in the S3 key — pulls up every leg of
    // that one message.
    const [filterTxn,      setFilterTxn]      = useState('');
    const [page, setPage] = useState(0);

    const PAGE_SIZE = 20;

    // TWO CALLS, AND THE SLOW ONE IS NOT AWAITED.
    //
    // The connectors come from the legacy DB and arrive in well under a second. The pipes come from an
    // Athena scan of the entire message log — the table is partitioned by connector_id alone, so
    // grouping by lambda across the chain has nothing to narrow it — and that took long enough that the
    // picker sat on "Loading…" with no connectors in it, which is the one thing the whole screen needs.
    //
    // So they are separate requests that resolve independently. The picker is usable the moment the
    // connectors land; the pipes section appears underneath them when it is ready. A pipes failure is
    // deliberately silent: it is a supplement, and a red error over a working connector list would be
    // reporting the wrong thing as broken.
    useEffect(() => {
        rotanaService.listConnectors()
            .then(d => {
                setConnectors(d.connectors || []);
                setRoutes(d.routes || []);
                setDbUser(d.dbUser || null);
            })
            .catch(err => {
                const msg = err.response?.data?.message;
                setConnectorsError(msg || 'Failed to load connectors');
            })
            .finally(() => setConnectorsLoading(false));

        rotanaService.listGateways()
            .then(d => setGateways(d.gateways || []))
            .catch(() => setGateways([]));
    }, []);

    // `overrides` exists for click-to-correlate: setState is asynchronous, so a handler that sets the
    // transaction-id filter and immediately reloads would otherwise send the PREVIOUS value and appear to
    // do nothing on the first click.
    const loadLogs = useCallback(async (connectorId, ids, overrides = {}) => {
        if (!connectorId) return;
        setLogsLoading(true);
        setLogsError(null);
        try {
            const d = await rotanaService.getLogs(connectorId, {
                messageType:    filterMsgType || undefined,
                payloadSearch:  filterPayload || undefined,
                transactionId:  filterTxn.trim() || undefined,
                connectorIds:   ids,
                lambda:         selectedLambda || undefined,
                ...overrides,
            });
            setLogs(d.logs || []);
            setPage(0);
        } catch {
            setLogsError('Failed to load logs');
        } finally {
            setLogsLoading(false);
        }
    }, [filterMsgType, filterPayload, filterTxn, selectedLambda]);

    const handleSelectConnector = (id) => {
        setSelectedId(id);
        setSelectedIds([id]);
        setSelectedLabel(null);
        setSelectedLambda(null);
        setLogs([]);
        loadLogs(id, [id]);
    };

    // A SOURCE IS THE WHOLE GROUP, not one connector.
    //
    // Records are filed under the connector that RECEIVED the message, so a channel's conversation is
    // split across the source and every target it routes to: what OHIP sent sits under OHIP, what
    // Pegasus received sits under Pegasus. Selecting the parent and seeing only its own half is how a
    // message that arrived and was never forwarded stays invisible — you have to already suspect the
    // target to go and look at it.
    //
    // The parent stays the PRIMARY id, so the query still addresses one connector and simply widens;
    // and it is what the rows are indented against below.
    const handleSelectSource = (g) => {
        const src = g.connector;
        const ids = [src.id, ...g.actions.flatMap(a => a.targets.map(t => t.id))];
        const unique = [...new Set(ids)];
        setSelectedId(src.id);
        setSelectedIds(unique);
        setSelectedLabel(null);
        setSelectedLambda(null);
        setLogs([]);
        loadLogs(src.id, unique);
    };

    // A PIPE: every record one lambda wrote, across the whole chain.
    //
    // Records are filed under the connector that received the message, so a pipe's traffic is spread
    // across every connector it serves — v1-hc-pegasus-enqueue writes under each Pegasus connector it
    // authenticates. Selecting a pipe therefore widens the query to all of them and filters by lambda,
    // which is the one view that answers "is this thing running at all".
    const handleSelectGateway = (gateway) => {
        const ids = connectors.map(c => c.id);
        if (!ids.length) return;
        setSelectedId(ids[0]);
        setSelectedIds(ids);
        setSelectedLabel(gateway.lambda);
        setSelectedLambda(gateway.lambda);
        setLogs([]);
        loadLogs(ids[0], ids, { lambda: gateway.lambda });
    };

    // A route: the source plus every target it fans out to. The source is the primary id, so a fan-out
    // route still addresses one connector and simply widens the query.
    const handleSelectRoute = (src, action) => {
        const ids = [src.id, ...action.targets.map(t => t.id)];
        setSelectedId(src.id);
        setSelectedIds(ids);
        setSelectedLabel(action.description || `Route ${action.actionId}`);
        setSelectedLambda(null);
        setLogs([]);
        loadLogs(src.id, ids);
    };

    // FOLLOW ONE MESSAGE ACROSS EVERY CONNECTOR.
    //
    // Not a navigation to the next connector's screen — a message may fan out to SEVERAL, so sending
    // the user to one of them would answer a question they did not ask. The list is replaced by every
    // record carrying this id, oldest hop first, each row already labelled with the connector that
    // wrote it.
    //
    // Trail mode lives in state rather than being derived from the filter box, so Refresh re-runs the
    // trail instead of quietly dropping back to the connector's own log.
    const handleFollow = (hcTransactionId) => {
        setTrail(hcTransactionId);
        setLogs([]);
        loadLogs(selectedId, selectedIds, { hcTransactionId });
    };

    const handleClearTrail = () => {
        setTrail(null);
        setLogs([]);
        loadLogs(selectedId, selectedIds);
    };

    const handleRefresh = () => (trail
        ? loadLogs(selectedId, selectedIds, { hcTransactionId: trail })
        : loadLogs(selectedId, selectedIds));

    // Show every record sharing this transaction id. Both legs of a message are filed under the SOURCE
    // connector (each worker logs under sourceConnectorId), so the related records are found by widening
    // the transaction, not by switching to the target connector — there is nothing there to switch to.
    // The route ids are used when a route is selected so a fan-out target's records are included too.
    const handleFilterByTxn = (txnId) => {
        if (!txnId) return;
        setFilterTxn(txnId);
        setPage(0);
        loadLogs(selectedId, selectedIds, { transactionId: txnId });
    };

    // The ticket is assembled SERVER-SIDE from the transaction id. Nothing about what the user has
    // expanded on screen is sent, because payloads are fetched per row on first expand — a ticket built
    // from client state would carry the rows they happened to open and quietly omit the rest.
    const submitJiraTicket = async () => {
        const txnId = jiraTxn;
        if (!txnId || jiraCreating) return;
        setJiraTxn(null);
        setJiraError(null);
        setJiraCreating(txnId);
        try {
            const res = await rotanaService.createJiraTicket(selectedId, txnId, {
                note: jiraNote.trim() || undefined,
                pageUrl: window.location.href,
                connectorIds: selectedIds,
            });
            setJiraTickets(prev => ({ ...prev, [txnId]: { key: res.key, url: res.url } }));
        } catch (err) {
            setJiraError({
                txnId,
                message: err.response?.data?.error?.message || err.response?.data?.message
                    || err.message || 'Failed to create Jira ticket',
            });
        } finally {
            setJiraCreating(null);
        }
    };

    const handlePurge = () => {
        setConfirmModal({
            title: 'Purge Connector Logs',
            message: `Delete all message log records for connector ${selectedId}? This cannot be undone.`,
            confirmLabel: 'Purge',
            danger: true,
            onConfirm: async () => {
                setPurging(true);
                try {
                    await rotanaService.purgeLogs(selectedId);
                    setLogs([]);
                } catch {
                    // silent — logs stay as-is
                } finally {
                    setPurging(false);
                }
            },
        });
    };

    const selected   = connectors.find(c => c.id === selectedId);
    const txnGroups  = groupByTransaction(logs);
    const totalPages = Math.ceil(txnGroups.length / PAGE_SIZE);
    const pagedGroups = txnGroups.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

    // WHAT THE HEADER SAYS IS SELECTED. A route or a pipe already carries its own label; a plain
    // connector is named from the list; and a source group says so, because "OHIP … DXBPH" alone would
    // not explain why Pegasus records are on screen.
    const pickerLabel = selectedLabel
        || (selectedId
            ? `${selected?.name || `Connector ${selectedId}`}${selectedIds.length > 1 ? ` + ${selectedIds.length - 1} linked` : ''}`
            : null);

    // Names for the block headers. Records carry a connector id and nothing else, and an id is not an
    // answer to "which system is this" for anyone who does not already know the estate.
    const connectorNames = useMemo(
        () => new Map(connectors.map(c => [String(c.id), c.name])),
        [connectors],
    );
    const connectorLabel = (id) => {
        const key = String(id ?? '');
        if (!key) return 'Unattributed';
        const name = connectorNames.get(key);
        return name ? `${name} #${key}` : `Connector #${key}`;
    };

    const tree = useMemo(() => buildConnectorTree(connectors, routes), [connectors, routes]);

    // Apply "Show inactive" + search to the tree. Inactive sources/actions/targets
    // are hidden unless showInactive; search matches source, any target, or an action name.
    const { visibleGroups, visibleUnrouted, visibleGateways } = useMemo(() => {
        const q = connectorSearch.trim().toLowerCase();
        // Searching connectorType is dropped along with the badge — every connector is 'INBOUND', so
        // typing "inbound" matched all of them and typing "outbound" matched none, neither of which is
        // a useful answer. Name, id, marketplace and lambda are what identify a connector here.
        const matches = (c) => (c.name || '').toLowerCase().includes(q) ||
            String(c.id || '').includes(q) ||
            (c.lambda || '').toLowerCase().includes(q) ||
            (c.marketplace || '').toLowerCase().includes(q);

        const groups = tree.sourceGroups
            .map(g => {
                const actions = g.actions
                    .map(a => ({ ...a, targets: a.targets.filter(t => showInactive || isConnectorActive(t.status)) }))
                    .filter(a => (showInactive || isConnectorActive(a.status)) && a.targets.length > 0);
                return { connector: g.connector, actions };
            })
            .filter(g => showInactive || isConnectorActive(g.connector.status))
            // A MATCH ON A TARGET SHOWS THAT TARGET, NOT ITS WHOLE GROUP.
            //
            // Finding a route by the system it feeds is worth keeping — that is how you locate the
            // DAILYPOINT link when all you know is the property. But a group kept for that reason used
            // to render every target it has, so searching AUHPRUAT returned the DXBPH source with its
            // Pegasus and Thynk routes underneath, none of which contain the text. It reads as a broken
            // search, and it buries the one row you were looking for.
            //
            // So: if the SOURCE matches, the group is shown whole — you asked for that connector. If it
            // was kept only because something beneath it matched, it is narrowed to just those, and the
            // source is there as the context that makes the target meaningful.
            .map(g => {
                if (!q || matches(g.connector)) return g;
                const actions = g.actions
                    .map(a => ((a.description || '').toLowerCase().includes(q)
                        ? a                                   // the action itself matched: keep its targets
                        : { ...a, targets: a.targets.filter(matches) }))
                    .filter(a => a.targets.length > 0);
                return { connector: g.connector, actions };
            })
            .filter(g => !q || matches(g.connector) || g.actions.length > 0);

        const unrouted = tree.unrouted.filter(c => {
            if (!showInactive && !isConnectorActive(c.status)) return false;
            return !q || matches(c);
        });

        // The pipes obey the search box too. They were an always-visible list before; in a dropdown an
        // unfiltered section would sit under the filtered ones and look like results that ignored you.
        const pipes = gateways.filter(g => !q || (g.lambda || '').toLowerCase().includes(q));

        return { visibleGroups: groups, visibleUnrouted: unrouted, visibleGateways: pipes };
    }, [tree, showInactive, connectorSearch, gateways]);

    return (
        <>
        <div className="rotana-page">
            <main className="rotana-main">
                {/* THE HEADER IS ALWAYS RENDERED, selection or not. It holds the picker now, and a
                    screen whose only control appears after you have used a control you cannot see is
                    the shape the removed sidebar was propping up. */}
                <div className="rotana-main-header">
                    <div className="rotana-main-title">
                        Rotana Message Logs{dbUser ? <span className="rotana-db-user"> ({dbUser})</span> : ''}
                    </div>
                    <div className="rotana-filters">
                        <ConnectorPicker
                            groups={visibleGroups}
                            unrouted={visibleUnrouted}
                            gateways={visibleGateways}
                            query={connectorSearch}
                            onQuery={setConnectorSearch}
                            showInactive={showInactive}
                            onShowInactive={setShowInactive}
                            label={pickerLabel}
                            loading={connectorsLoading}
                            error={connectorsError}
                            onPickGroup={handleSelectSource}
                            onPickConnector={handleSelectConnector}
                            onPickGateway={handleSelectGateway}
                        />
                        <select
                                    className="rotana-filter-select"
                                    value={filterMsgType}
                                    onChange={e => setFilterMsgType(e.target.value)}
                                >
                                    <option value="">All message types</option>
                                    <option value="RESERVATION">Reservation</option>
                                    <option value="PROFILE">Profile</option>
                                    <option value="RATE">Rate</option>
                                    <option value="RATEPLAN">Rate Plan</option>
                                    <option value="RATESCHEDULE">Rate Schedule</option>
                                    <option value="FETCHBUSINESSEVENTS">Fetch Business Events</option>
                                    <option value="UPDATE_EXTERNAL_REFERENCE">Update External Reference</option>
                                </select>
                                <input
                                    className="rotana-filter-input"
                                    placeholder="Transaction id…"
                                    title="Full or partial transaction id"
                                    value={filterTxn}
                                    onChange={e => setFilterTxn(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && handleRefresh()}
                                    style={{ width: 190 }}
                                />
                                <input
                                    className="rotana-filter-input"
                                    placeholder="Search payload…"
                                    value={filterPayload}
                                    onChange={e => setFilterPayload(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && handleRefresh()}
                                    style={{ width: 160 }}
                                />
                                <button className="rotana-search-btn" onClick={handleRefresh} disabled={logsLoading}>
                                    Search
                                </button>
                                <button className="rotana-refresh-btn" onClick={handleRefresh} disabled={logsLoading}>
                                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                                        <path d="M13.65 2.35A8 8 0 1 0 15 8h-2a6 6 0 1 1-1.76-4.24L9 6h6V0l-1.35 2.35z" fill="currentColor"/>
                                    </svg>
                                    Refresh
                                </button>
                                {isSuperAdmin && !selectedLabel && (
                                    <button className="pp-criteria-add-btn pp-criteria-add-btn-danger sa-only" onClick={handlePurge} disabled={purging || logsLoading}>
                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>
                                            <polyline points="3 6 5 6 21 6"/>
                                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                                            <path d="M10 11v6M14 11v6"/>
                                            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                                        </svg>
                                        {purging ? 'Purging…' : 'Purge'}
                                    </button>
                        )}
                    </div>
                </div>

                {!selectedId ? (
                    <div className="rotana-select-prompt">Choose a connector above to view its logs.</div>
                ) : (
                    <div className="rotana-log-area">
                        {/* Trail mode has to announce itself. The list looks like an ordinary log but
                            is scoped to one message across every connector, and rows from a connector
                            the user did not select would otherwise read as a bug. */}
                        {trail && (
                            <div className="rotana-trail-banner">
                                <span className="rotana-trail-label">Following one message</span>
                                <code className="rotana-trail-id">{trail}</code>
                                <span className="rotana-trail-hint">
                                    every connector that handled it, oldest hop first
                                </span>
                                <button type="button" className="rotana-trail-clear" onClick={handleClearTrail}>
                                    ← Back to this connector
                                </button>
                            </div>
                        )}
                        {logsLoading && <div className="rotana-loading">Loading logs…</div>}
                        {logsError   && <div className="rotana-error">{logsError}</div>}

                        {!logsLoading && !logsError && logs.length === 0 && (
                            <div className="rotana-empty">No logs found for this connector.</div>
                        )}

                        {!logsLoading && !logsError && pagedGroups.map(([txnId, entries]) => (
                            <div className="rotana-log-group" key={txnId}>
                                <div className="rotana-log-group-header">
                                    <span>
                                        Transaction:{' '}
                                        <button
                                            type="button"
                                            className="rotana-txn-link"
                                            title="Show every leg of this message"
                                            onClick={() => handleFilterByTxn(txnId)}
                                        >
                                            {txnId}
                                        </button>
                                    </span>
                                    {/* ON THE PARENT, ABOUT THE WHOLE TREE. One transaction is one event
                                        however many connectors handled it, so the ticket belongs to the
                                        group header rather than to any single row. */}
                                    {isSuperAdmin && (
                                        jiraTickets[txnId] ? (
                                            <a
                                                className="rotana-jira-link sa-only"
                                                href={jiraTickets[txnId].url}
                                                target="_blank"
                                                rel="noreferrer"
                                                title="Open the Jira ticket raised for this transaction"
                                            >
                                                {jiraTickets[txnId].key}
                                            </a>
                                        ) : (
                                            <button
                                                type="button"
                                                className="rotana-jira-btn sa-only"
                                                disabled={jiraCreating === txnId}
                                                title="Raise a Jira ticket for this transaction and every connector that handled it"
                                                onClick={() => { setJiraNote(''); setJiraError(null); setJiraTxn(txnId); }}
                                            >
                                                {jiraCreating === txnId ? 'Creating…' : 'Jira'}
                                            </button>
                                        )
                                    )}
                                </div>
                                {jiraError?.txnId === txnId && (
                                    <div className="rotana-error rotana-jira-error">{jiraError.message}</div>
                                )}
                                {/* ONE BLOCK PER CONNECTOR, NAMED. The block header is the only place
                                    on this screen that says in words which system a record belongs to;
                                    without it the answer is a bare id at the end of the line. */}
                                {partitionByConnector(pairLogs(entries), selectedId).map(part => (
                                    <div
                                        className={`rotana-log-part${String(part.connectorId) === String(selectedId) ? ' rotana-log-part-primary' : ''}`}
                                        key={part.connectorId || 'unknown'}
                                    >
                                        <div className="rotana-log-part-header">
                                            <span className="rotana-log-part-name">{connectorLabel(part.connectorId)}</span>
                                            <span className="rotana-log-part-count">
                                                {part.pairs.length} message{part.pairs.length === 1 ? '' : 's'}
                                            </span>
                                        </div>
                                        {part.pairs.map((pair, i) => (
                                            <LogPairRow key={i} pair={pair} connectorId={selectedId} connectorIds={selectedIds} onFollow={handleFollow} />
                                        ))}
                                    </div>
                                ))}
                            </div>
                        ))}
                        {!logsLoading && !logsError && totalPages > 1 && (
                            <div className="rotana-pagination">
                                <button
                                    className="rotana-page-btn"
                                    onClick={() => setPage(p => p - 1)}
                                    disabled={page === 0}
                                >‹ Prev</button>
                                <span className="rotana-page-info">
                                    Page {page + 1} of {totalPages}
                                    <span className="rotana-page-total"> ({txnGroups.length} transactions)</span>
                                </span>
                                <button
                                    className="rotana-page-btn"
                                    onClick={() => setPage(p => p + 1)}
                                    disabled={page >= totalPages - 1}
                                >Next ›</button>
                            </div>
                        )}
                    </div>
                )}
            </main>
        </div>
        {jiraTxn && (
            <ConfirmModal
                title="Create Jira ticket"
                message={`A ticket for transaction ${jiraTxn} will be created with every connector that handled it, `
                    + 'its child records and their payloads attached. Describe the change you want:'}
                confirmLabel="Create ticket"
                onConfirm={submitJiraTicket}
                onCancel={() => setJiraTxn(null)}
            >
                <textarea
                    className="cm-textarea"
                    value={jiraNote}
                    onChange={e => setJiraNote(e.target.value)}
                    placeholder="e.g. The external reference is not being written back to Pegasus on update"
                    autoFocus
                />
            </ConfirmModal>
        )}
        {confirmModal && (
            <ConfirmModal
                title={confirmModal.title}
                message={confirmModal.message}
                confirmLabel={confirmModal.confirmLabel}
                danger={confirmModal.danger}
                onConfirm={() => { confirmModal.onConfirm(); setConfirmModal(null); }}
                onCancel={() => setConfirmModal(null)}
            />
        )}
        </>
    );
};

export default RotanaPage;
